import { inject, Injectable } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { OrderDialogComponent } from './order-dialog/order-dialog.component';
import { OrderResponse } from './order.service';
import { ServiceConfigurationService } from '../service-configuration/service-configuration.service';

/** Shared so the toolbar's "Baum bestellen" button, the landing page tile and the Kundendashboard's
 * "Weiterer Auftrag" tile all trigger the same dialog. */
@Injectable({
  providedIn: 'root'
})
export class OrderDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly serviceConfigurationService = inject(ServiceConfigurationService);

  private readonly orderCreatedSubject = new Subject<OrderResponse>();
  /** Fires for every order created through the dialog, whoever opened it (toolbar, landing page, dashboard) -
   * the Kundendashboard listens so a just-ordered (or just-registered-and-ordered) tile shows up without a reload. */
  readonly orderCreated$ = this.orderCreatedSubject.asObservable();

  /** Pulls in the qrcode package, which isn't tree-shakeable, so this is loaded on demand too. Returns the
   * dialog ref (once opened) so callers that care about the created order - e.g. the Kundendashboard, which
   * wants to append it to the tile grid without a full reload - can subscribe to afterClosed themselves.
   * Customer-facing callers get a fresh customerService check first (the page may have been loaded long
   * before the switch was turned off): resolves to null, with a snackbar, instead of opening a dialog that
   * could no longer be submitted. Staff recording orders on the customers' behalf pass skipServiceCheck -
   * staff may keep working while the customer area is paused. */
  async open(skipServiceCheck = false): Promise<MatDialogRef<OrderDialogComponent, OrderResponse | null> | null> {
    if (!skipServiceCheck) {
      const status = await firstValueFrom(this.serviceConfigurationService.getCustomerServiceStatus())
        .catch(() => ({enabled: true, message: null}));
      if (!status.enabled) {
        // No auto-dismiss: confirming reloads the page so the maintenance notice (landing page) shows up.
        this.snackBar.open('Der Kundenbereich ist derzeit nicht verfügbar.', 'Ok')
          .onAction().subscribe(() => window.location.reload());
        return null;
      }
    }
    const {OrderDialogComponent} = await import('./order-dialog/order-dialog.component');
    const dialogRef = this.dialog.open(OrderDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      panelClass: 'order-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(order => {
      if (order) {
        this.orderCreatedSubject.next(order);
        this.snackBar.open('Bestellung wurde aufgenommen.', 'Ok', {duration: 4000});
      }
    });

    return dialogRef;
  }
}
